import { useCallback, useEffect, useRef } from 'react'
import type { ApolloError } from '@apollo/client'
import { useMutation, useQuery } from '@truepill/tpos-react-router'
import { CLEAR_PRIOR_AUTHORIZATION_LOCK, GET_PRIOR_AUTHORIZATION_LOCK, SET_PRIOR_AUTHORIZATION_LOCK } from 'gql'
import type { GraphQLError } from 'graphql'
import { usePlusClient } from 'providers/VisionRouter'
import { isNil } from 'ramda'
import type { Lock, PriorAuthorization, User, UserFirstLastName } from 'types'

type LockWithPartialUser = Omit<Lock, 'user'> & { user: Pick<User, '_id' | 'firstName' | 'lastName'> }
type priorAuthorizationLockMutationResponse = Pick<PriorAuthorization, '_id'> & { lock: LockWithPartialUser }

interface PriorAuthorizationLock {
  priorAuthorizationEditable: boolean
  priorAuthorizationLock?: LockWithPartialUser
  priorAuthorizationLockedBy?: UserFirstLastName | null
  isPriorAuthorizationLockedByMe: boolean
  setPriorAuthorizationLock: () => Promise<void>
  clearPriorAuthorizationLock: () => Promise<LockWithPartialUser | undefined>
}

export default function usePriorAuthorizationLock(
  priorAuthorizationId?: string,
  shouldSkip = false,
): PriorAuthorizationLock {
  const forbiddenRef = useRef(false)
  // Reset forbiddenRef when priorAuthorizationId changes
  useEffect(() => {
    forbiddenRef.current = false
  }, [priorAuthorizationId])
  const { tokenContext } = usePlusClient()

  const { loading, error, data } = useQuery<{ priorAuthorization: { lock?: LockWithPartialUser } }>(
    GET_PRIOR_AUTHORIZATION_LOCK,
    {
      skip: isNil(priorAuthorizationId) || shouldSkip,
      variables: { priorAuthorizationId },
    },
  )

  const [setPriorAuthorizationLockMutation, { error: priorAuthorizationLockError }] = useMutation<{
    copay?: priorAuthorizationLockMutationResponse
  }>(SET_PRIOR_AUTHORIZATION_LOCK)

  const [clearPriorAuthorizationLockMutation, { data: paClearLockData, error: priorAuthorizationClearError }] =
    useMutation<{
      copay?: priorAuthorizationLockMutationResponse
    }>(CLEAR_PRIOR_AUTHORIZATION_LOCK)

  const setPriorAuthorizationLock = useCallback(async () => {
    // Avoid trying to get lock multiple times when getting GraphQL Forbidden errors
    if (!priorAuthorizationId || forbiddenRef.current) {
      return
    }
    try {
      await setPriorAuthorizationLockMutation({ variables: { priorAuthorizationId } })
    } catch (error) {
      const isForbiddenError = (error as ApolloError)?.graphQLErrors?.find(
        (err: GraphQLError & { extensions?: { code?: string } }) => err.extensions?.code === 'FORBIDDEN',
      )
      if (isForbiddenError) {
        forbiddenRef.current = true
      }
      throw error
    }

    if (priorAuthorizationLockError) {
      throw new Error(priorAuthorizationLockError.toString())
    }
  }, [priorAuthorizationId, setPriorAuthorizationLockMutation, priorAuthorizationLockError])

  const clearPriorAuthorizationLock = async () => {
    if (!priorAuthorizationId) {
      return
    }

    await clearPriorAuthorizationLockMutation({ variables: { priorAuthorizationId } })

    if (priorAuthorizationClearError) {
      throw new Error(priorAuthorizationClearError.toString())
    }

    return paClearLockData?.copay?.lock
  }

  const priorAuthorizationLockedBy = data?.priorAuthorization ? data.priorAuthorization.lock?.user ?? null : undefined
  const isPriorAuthorizationLockedByMe = data?.priorAuthorization?.lock?.user._id === tokenContext?.id
  const priorAuthorizationEditable =
    !priorAuthorizationId || (!!tokenContext && (!data?.priorAuthorization?.lock || isPriorAuthorizationLockedByMe))

  return {
    priorAuthorizationEditable,
    isPriorAuthorizationLockedByMe,
    priorAuthorizationLockedBy,
    priorAuthorizationLock:
      loading || error || !data || (data.priorAuthorization?.lock?.expiresAt ?? -1) < Date.now()
        ? undefined
        : data.priorAuthorization?.lock,
    setPriorAuthorizationLock,
    clearPriorAuthorizationLock,
  }
}
